import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, CardBody, Input } from 'reactstrap';
import { toast } from "react-toastify";
import moment from 'moment';
import _ from 'lodash';
import { FilterDateTime, ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, dateHelpers } from '../utils';

// Admin Tools > Event Log. One call (Reference/GetLogEvents) returns the failures (one per request, with
// its layers inside), or the flat rows, or the failed sign-ins - plus the tile counts, which cover the
// whole date window and ignore the chips.

const DATE_FORMAT = dateHelpers.MDYHM24;
const REQUEST_DATE_FORMAT = "YYYY-MM-DD";
const SCREEN_NAME = "Event Log";

// Server caps (api-contract.md); only used to word the truncation note.
const MAX_FAILURES = 200;
const MAX_ROWS = 1000;
const MAX_SIGN_INS = 200;

const LOAD_FAILED_MESSAGE = "Unable to load the event log.";
const COPY_FAILED_MESSAGE = "Copy was blocked by the browser.";
const KEEP_ONE_SEVERITY_MESSAGE = "Keep at least one of Errors, Warnings or Info selected.";
const STALE_RESULT_MESSAGE = "The list below is from the previous filters.";
// The tiles total each kind for the dates shown, whichever kinds are listed (a search narrows only the list).
const COUNTS_WINDOW_NOTE = "Totals for the dates shown";
const COUNTS_SHORTCUT_NOTE = "Totals for this request";
const LOOKUP_INVALID_MESSAGE = "Enter a Log # or a request id.";
const LOOKUP_RETURN_NOTE = "Press Clear next to the Log # box to return to the filtered list.";

// The lookup kinds the server reports (AppConstants.EventLogViewer.Shortcut - wire values, change both together).
const SHORTCUT = { RequestId: "requestId", LogId: "logId" };

// The lookup box takes a Log # (digits only) or a request id in any form .NET's Guid.TryParse reads: hyphenated,
// 32 hex digits, or hyphenated inside braces or parentheses. The server binds only the hyphenated form, so a request id
// is normalized before it is sent.
const LOG_ID_PATTERN = /^\d+$/;
const GUID_HYPHENATED = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const GUID_PATTERN = new RegExp(`^(?:${GUID_HYPHENATED}|\\{${GUID_HYPHENATED}\\}|\\(${GUID_HYPHENATED}\\)|[0-9a-f]{32})$`, "i");
const GUID_GROUP_LENGTHS = [8, 4, 4, 4, 12];
const NO_LOOKUP = { logId: null, requestId: null };

const SEVERITY = {
   Errors: "errors",
   Warnings: "warnings",
   Info: "info",
   SignIns: "signIns",
   Instron: "instron",
};

// The exclusive modes: the tile / chip key and the criteria flag it switches.
const MODE_FLAG = {
   [SEVERITY.SignIns]: "signIns",
   [SEVERITY.Instron]: "instronOnly",
};

// Tile / chip display order. badge is the singular label shown on a row; chipClass colors the chip (Info stays plain).
const SEVERITY_INFO = [
   { key: SEVERITY.Errors, label: "Errors", badge: "Error", cssClass: "red", chipClass: "red" },
   { key: SEVERITY.Warnings, label: "Warnings", badge: "Warning", cssClass: "amber", chipClass: "amber" },
   { key: SEVERITY.SignIns, label: "Sign-in", badge: "Sign-in", cssClass: "blue", chipClass: "blue" },
   { key: SEVERITY.Instron, label: "Instron", badge: "Instron", cssClass: "idle", chipClass: "" },
   { key: SEVERITY.Info, label: "Info", badge: "Info", cssClass: "idle", chipClass: "" },
];
const LIST_SEVERITIES = [SEVERITY.Errors, SEVERITY.Warnings, SEVERITY.Info];

// TypeOfLogError ids: 0 Severe, 1 Normal, 2 Minor, 3 Info, 4 Authentication (ranked as an error).
const WARNING_TYPE_ID = 2;
const INFO_TYPE_ID = 3;

const LAYER_CONTROLLER = "controller";
// The procedure name of the impersonation attribution row (AppConstants.Impersonation.ACTION_LOG_SOURCE - change both together).
const IMPERSONATION_LOG_SOURCE = "Impersonation.Action";
const START_DATE_FILTER = "StartDate";
const END_DATE_FILTER = "EndDate";

// Two plain dates (Carolyn 2026-10-15: simple is better - no range chips). The start defaults to today; a blank end
// means "through now", so moving the start back a day after a day off is the only edit needed. A function, not a
// constant, so a tab left open overnight still defaults to the day the screen is opened.
const DEFAULT_SEVERITIES = [SEVERITY.Errors, SEVERITY.Warnings];
const defaultCriteria = () => ({
   ...NO_LOOKUP,   // set only by the lookup box; any filter change drops it
   severities: DEFAULT_SEVERITIES,
   signIns: false,
   startDate: moment().format(REQUEST_DATE_FORMAT),
   endDate: "",
   instronOnly: false,
   groupByRequest: true,
   search: "",
});

const EMPTY_RESULT = {
   failures: [],
   rows: [],
   signIns: [],
   counts: { errors: 0, warnings: 0, info: 0, signIns: 0, instron: 0 },
   truncated: false,
   shortcut: null,
};

function formatDate(value) {
   return value ? dateHelpers.dateTimeFormat(value, DATE_FORMAT) : "-";
}

// "1 failed sign-in", "2 failed sign-ins".
function countOf(count, singular, plural) {
   return `${count} ${count === 1 ? singular : plural}`;
}

function severityInfo(key) {
   return _.find(SEVERITY_INFO, (s) => s.key === key) || _.find(SEVERITY_INFO, (s) => s.key === SEVERITY.Info);
}

// A flat row carries only its TypeOfLogError id.
function severityOfType(typeId) {
   if (typeId === WARNING_TYPE_ID) return SEVERITY.Warnings;
   if (typeId === INFO_TYPE_ID) return SEVERITY.Info;
   return SEVERITY.Errors;
}

// A date picker reports every keystroke; only a complete date is worth asking the server about.
function isCompleteDate(value) {
   return moment(value, REQUEST_DATE_FORMAT, true).isValid();
}

// Ask only for a complete range - a start date, and an end that is blank or complete.
function isCompleteRange(criteria) {
   return isCompleteDate(criteria.startDate) && (!criteria.endDate || isCompleteDate(criteria.endDate));
}

function hasLookup(criteria) {
   return criteria.logId !== null || criteria.requestId !== null;
}

// The lookup fields for the box's text ({ logId } or { requestId }), or null when it is neither form.
function parseLookup(text) {
   if (LOG_ID_PATTERN.test(text)) {
      const logId = Number(text);
      return Number.isSafeInteger(logId) ? { ...NO_LOOKUP, logId } : null;
   }
   if (!GUID_PATTERN.test(text)) return null;
   const hex = text.replace(/[^0-9a-f]/gi, "").toLowerCase();
   let start = 0;
   const groups = _.map(GUID_GROUP_LENGTHS, (length) => {
      const group = hex.slice(start, start + length);
      start += length;
      return group;
   });
   return { ...NO_LOOKUP, requestId: groups.join("-") };
}

function buildPayload(criteria) {
   return {
      startDate: criteria.startDate || null,
      endDate: criteria.endDate || null,   // blank = no upper bound (through now)
      severities: criteria.severities,
      signIns: criteria.signIns,
      instronOnly: criteria.instronOnly,
      search: criteria.search,
      groupByRequest: criteria.groupByRequest,
      logId: criteria.logId,
      requestId: criteria.requestId,
   };
}

function shortcutNote(shortcut, shown) {
   const found = shortcut === SHORTCUT.RequestId
      ? `Showing request ${shown.requestId}.`
      : `Showing Log # ${shown.logId} and the rest of its request.`;
   return `${found} The date range, filters and search were bypassed. ${LOOKUP_RETURN_NOTE}`;
}

function truncationNote(criteria) {
   if (criteria.instronOnly) return `Showing the newest ${MAX_ROWS.toLocaleString()} Instron rows - narrow the dates.`;
   if (criteria.signIns) return `Showing the newest ${MAX_SIGN_INS} failed sign-ins - narrow the dates.`;
   if (!criteria.groupByRequest) return `Showing the newest ${MAX_ROWS.toLocaleString()} rows - narrow the dates.`;
   return `Showing the newest ${MAX_FAILURES} failures - narrow the dates.`;
}

function copyToClipboard(text) {
   if (!navigator.clipboard) {
      toast.error(COPY_FAILED_MESSAGE);
      return;
   }
   navigator.clipboard.writeText(text).then(
      () => toast.success(`Copied ${text}`),
      () => toast.error(COPY_FAILED_MESSAGE)
   );
}

// Enter or Space on a role="button" row does what a click does.
function onActivateKey(e, action) {
   if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
   }
}


export default function EventViewer() {
   const [criteria, setCriteria] = useState(defaultCriteria);
   const [searchText, setSearchText] = useState("");
   const [lookupText, setLookupText] = useState("");
   const [result, setResult] = useState(EMPTY_RESULT);
   const [shown, setShown] = useState(defaultCriteria);   // the criteria the displayed result was loaded with
   const [loaded, setLoaded] = useState(false);
   const [asOf, setAsOf] = useState(null);

   // Only the newest request may update the screen; Refresh is ignored while one is running.
   const latestRequestId = useRef(0);
   const inFlight = useRef(false);

   // The server owns the validation (ReferenceController.GetLogEvents) and answers a refusal with its own sentence.
   function loadEvents(next) {
      const requestId = ++latestRequestId.current;
      inFlight.current = true;
      setLoaded(false);
      api
         .post("Reference/GetLogEvents", buildPayload(next))
         .then((response) => {
            if (requestId !== latestRequestId.current) return;
            // A failed call keeps the last good result on screen.
            if (api.toastFailure(response, LOAD_FAILED_MESSAGE)) return;
            setResult({ ...EMPTY_RESULT, ...response.data });
            setShown(next);
            setAsOf(new Date());
         })
         .catch((error) => {
            if (requestId !== latestRequestId.current) return;
            api.catchHandler(error);
            toast.error(LOAD_FAILED_MESSAGE);
         })
         .finally(() => {
            if (requestId !== latestRequestId.current) return;
            inFlight.current = false;
            setLoaded(true);
         });
   }

   // The one request on mount.
   useEffect(() => {
      document.title = SCREEN_NAME;
      loadEvents(criteria);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // Every filter control goes through here. Changing a filter means "back to the filtered list", so it drops a
   // lookup (which would otherwise keep ignoring the change) and empties the lookup box with it.
   function applyCriteria(next) {
      const filtered = { ...next, ...NO_LOOKUP };
      if (hasLookup(criteria)) setLookupText("");
      setCriteria(filtered);
      loadEvents(filtered);
   }

   // A lookup ignores the dates, chips, Instron rule and search, so it is never held back by a half-typed date. It
   // turns Sign-in off (the server refuses a lookup with it) and Instron off, restoring the default chips either cleared.
   function onFind() {
      const text = lookupText.trim();
      if (!text && hasLookup(criteria)) {
         onClearLookup();
         return;
      }
      const lookup = parseLookup(text);
      if (!lookup) {
         toast.warning(LOOKUP_INVALID_MESSAGE);
         return;
      }
      const next = {
         ...criteria,
         ...lookup,
         signIns: false,
         instronOnly: false,
         severities: criteria.signIns || criteria.instronOnly ? DEFAULT_SEVERITIES : criteria.severities,
      };
      setCriteria(next);
      loadEvents(next);
   }

   // Back to the filtered list, reloaded - unless a date is half typed, when the stale-list note says the list is older.
   function onClearLookup() {
      setLookupText("");
      if (!hasLookup(criteria)) return;
      const next = { ...criteria, ...NO_LOOKUP };
      if (isCompleteRange(next)) applyCriteria(next);
      else setCriteria(next);
   }

   // Sign-in and Instron are exclusive modes, and exclusive of each other: selecting one clears the severity chips and
   // the other mode, selecting a severity clears either. Clicking the active mode again turns it off and restores the
   // default chips (Carolyn 2026-10-22), the same chips the lookup box restores.
   function onSeverityClick(key) {
      const modeFlag = MODE_FLAG[key];
      if (modeFlag) {
         if (criteria[modeFlag]) {
            applyCriteria({ ...criteria, [modeFlag]: false, severities: DEFAULT_SEVERITIES });
            return;
         }
         applyCriteria({ ...criteria, signIns: false, instronOnly: false, [modeFlag]: true, severities: [] });
         return;
      }
      if (criteria.signIns || criteria.instronOnly) {
         applyCriteria({ ...criteria, signIns: false, instronOnly: false, severities: [key] });
         return;
      }
      const isOn = _.includes(criteria.severities, key);
      if (isOn && criteria.severities.length === 1) {
         toast.warning(KEEP_ONE_SEVERITY_MESSAGE);
         return;
      }
      const severities = isOn
         ? _.without(criteria.severities, key)
         : _.filter(LIST_SEVERITIES, (s) => s === key || _.includes(criteria.severities, s));
      applyCriteria({ ...criteria, severities });
   }

   function onDateChange(change) {
      const value = change.value || "";
      const next = change.filterName === START_DATE_FILTER
         ? { ...criteria, startDate: value }
         : { ...criteria, endDate: value };
      // Ask only for a complete range. Anything else (a cleared start, a date half typed) just updates the pickers -
      // dropping a lookup like any filter change - and the stale-list note says the list is older.
      if (isCompleteRange(next)) applyCriteria(next);
      else {
         if (hasLookup(criteria)) setLookupText("");
         setCriteria({ ...next, ...NO_LOOKUP });
      }
   }

   function onSearch() {
      applyCriteria({ ...criteria, search: searchText.trim() });
   }

   function onRefresh() {
      if (inFlight.current) return;
      loadEvents(criteria);
   }

   const listCount = result.failures.length;
   let summaryText = "";
   if (shown.signIns) summaryText = `${countOf(result.signIns.length, "failed sign-in", "failed sign-ins")}, newest first`;
   else if (shown.instronOnly) summaryText = `${countOf(result.rows.length, "Instron row", "Instron rows")}, newest first`;
   else if (!shown.groupByRequest) summaryText = `${countOf(result.rows.length, "row", "rows")}, newest first`;
   else summaryText = `${countOf(listCount, "failure", "failures")}, newest first`;

   return (
      <DesktopContainer screenName={SCREEN_NAME}>
         <div className="event-log-page">
            <Card className="dashboard-card">
               <CardBody>
                  <div className="d-flex flex-wrap align-items-center justify-content-between">
                     <span className="h2 me-2">{SCREEN_NAME}</span>
                     <span className="event-log-secondary">
                        {asOf ? `as of ${formatDate(asOf)}` : ""}
                        {asOf ? <span className="ms-3">{summaryText}</span> : null}
                     </span>
                  </div>
                  <hr />
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                     {_.map(SEVERITY_INFO, (info) => (
                        <SummaryTile
                           key={info.key}
                           info={info}
                           count={result.counts[info.key] ?? 0}
                           active={isSeverityActive(criteria, info.key)}
                           onClick={onSeverityClick}
                        />
                     ))}
                     <span className="event-log-secondary">
                        {/* Describes the counts on screen, so it reads the loaded result (shown), not the pending filters. */}
                        {result.shortcut ? COUNTS_SHORTCUT_NOTE : COUNTS_WINDOW_NOTE}
                     </span>
                  </div>

                  <FilterBar
                     criteria={criteria}
                     searchText={searchText}
                     onSearchTextChange={setSearchText}
                     onSeverityClick={onSeverityClick}
                     onDateChange={onDateChange}
                     onToggleEveryRow={() => applyCriteria({ ...criteria, groupByRequest: !criteria.groupByRequest })}
                     onSearch={onSearch}
                     onRefresh={onRefresh}
                     lookupText={lookupText}
                     onLookupTextChange={setLookupText}
                     onFind={onFind}
                     onClearLookup={onClearLookup}
                  />

                  {loaded && !_.isEqual(criteria, shown) ? (
                     <div className="event-log-secondary event-log-note">{STALE_RESULT_MESSAGE}</div>
                  ) : null}
                  {result.shortcut ? (
                     <div className="event-log-note">{shortcutNote(result.shortcut, shown)}</div>
                  ) : null}

                  <ServiceThreadSpinner on={!loaded}>
                     <ResultList result={result} shown={shown} loaded={loaded} />
                     {result.truncated ? (
                        <div className="event-log-secondary event-log-note">{truncationNote(shown)}</div>
                     ) : null}
                  </ServiceThreadSpinner>
               </CardBody>
            </Card>
         </div>
      </DesktopContainer>
   );
}


function isSeverityActive(criteria, key) {
   if (MODE_FLAG[key]) return criteria[MODE_FLAG[key]];
   return !criteria.signIns && !criteria.instronOnly && _.includes(criteria.severities, key);
}


function SummaryTile({ info, count, active, onClick }) {
   return (
      <button
         type="button"
         className={`event-log-summary ${info.cssClass}${active ? " active" : ""}`}
         aria-pressed={active}
         onClick={() => onClick(info.key)}
      >
         <span className="event-log-summary-count">{count}</span>
         <span>{info.label}</span>
      </button>
   );
}


function FilterChip({ label, pressed, cssClass, dot, onClick }) {
   return (
      <button
         type="button"
         className={`event-log-chip${cssClass ? ` ${cssClass}` : ""}`}
         aria-pressed={pressed}
         onClick={onClick}
      >
         {dot ? <span className={`event-log-dot ${cssClass || ""}`} /> : null}
         {label}
      </button>
   );
}


function FilterBar({
   criteria, searchText, onSearchTextChange, onSeverityClick, onDateChange,
   onToggleEveryRow, onSearch, onRefresh,
   lookupText, onLookupTextChange, onFind, onClearLookup,
}) {
   return (
      <div className="event-log-filters">
         <div className="event-log-filter-group" role="group" aria-label="Severity">
            <span className="event-log-filter-label">Show</span>
            {_.map(SEVERITY_INFO, (info) => (
               <FilterChip
                  key={info.key}
                  label={info.label}
                  cssClass={info.chipClass}
                  dot
                  pressed={isSeverityActive(criteria, info.key)}
                  onClick={() => onSeverityClick(info.key)}
               />
            ))}
         </div>
         <div className="event-log-filter-group" role="group" aria-label="Dates">
            <span className="event-log-filter-label">From</span>
            <span className="event-log-date">
               <FilterDateTime filterName={START_DATE_FILTER} value={criteria.startDate} onChangeCallback={onDateChange} />
            </span>
            <span className="event-log-filter-label">to</span>
            <span className="event-log-date" title="Leave blank for everything through now">
               <FilterDateTime filterName={END_DATE_FILTER} value={criteria.endDate} onChangeCallback={onDateChange} />
            </span>
         </div>
         <div className="event-log-filter-group">
            {/* Hidden, not disabled, while the sign-in or Instron list is up - it switches only the failure and row lists. */}
            {criteria.signIns || criteria.instronOnly
               ? null
               : <FilterChip label="Show every row" pressed={!criteria.groupByRequest} onClick={onToggleEveryRow} />}
         </div>
         {/* Both text boxes share a line of their own below the filters: the search first, the exact lookup to its right. */}
         <div className="event-log-search-row">
            <div className="event-log-search">
               <Input
                  type="text"
                  aria-label="Search the log"
                  placeholder="Search message, user, screen or item"
                  value={searchText}
                  onChange={(e) => onSearchTextChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
               />
               <Button color="primary" onClick={onSearch}>Search</Button>
               <Button color="secondary" onClick={onRefresh}>Refresh</Button>
            </div>
            <LookupBox text={lookupText} onTextChange={onLookupTextChange} onFind={onFind} onClear={onClearLookup} />
         </div>
      </div>
   );
}


// The exact lookups - the only way past the date range. Kept apart from the search box so a number typed there
// (an order number) is always searched as text.
function LookupBox({ text, onTextChange, onFind, onClear }) {
   return (
      <div className="event-log-lookup" role="group" aria-label="Find a Log # or request id">
         <Input
            type="text"
            aria-label="Log # or request id"
            placeholder="Find Log # or request id"
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onFind(); }}
         />
         <Button color="primary" onClick={onFind}>Find</Button>
         <Button color="secondary" onClick={onClear}>Clear</Button>
      </div>
   );
}


// Picks the view the loaded result belongs to (shown = the criteria that produced it).
function ResultList({ result, shown, loaded }) {
   if (shown.signIns) {
      return <SignInList signIns={result.signIns} loaded={loaded} />;
   }
   if (shown.instronOnly || !shown.groupByRequest) {
      const emptyText = shown.instronOnly ? "No Instron rows for these dates." : "No rows match these filters.";
      return <RowList rows={result.rows} loaded={loaded} emptyText={emptyText} />;
   }
   return <FailureList failures={result.failures} loaded={loaded} />;
}


function EmptyNote({ loaded, text }) {
   return loaded ? <div className="event-log-empty">{text}</div> : null;
}


function SeverityBadge({ severity }) {
   const info = severityInfo(severity);
   return (
      <span className="event-log-severity">
         <span className={`event-log-dot ${info.cssClass}`} />
         {info.badge}
      </span>
   );
}


function FailureList({ failures, loaded }) {
   if (failures.length === 0) {
      return <EmptyNote loaded={loaded} text="No failures match these filters." />;
   }
   return (
      <div>
         <div className="event-log-columns failures" aria-hidden="true">
            <div className="event-log-cell severity">Severity</div>
            <div className="event-log-cell time">Time</div>
            <div className="event-log-cell user">User</div>
            <div className="event-log-cell screen">Screen / action</div>
            <div className="event-log-cell saw">What the user saw</div>
            <div className="event-log-cell item">Item</div>
            <div className="event-log-cell entries" />
         </div>
         {_.map(failures, (failure) => (
            <FailureItem key={failure.key} failure={failure} />
         ))}
      </div>
   );
}


function FailureItem({ failure }) {
   const [open, setOpen] = useState(false);
   const detailId = `event-log-${failure.key}`;
   const toggle = () => setOpen((prior) => !prior);
   return (
      <div className="event-log-failure">
         <div
            className="event-log-row failures"
            role="button"
            tabIndex={0}
            aria-expanded={open}
            aria-controls={detailId}
            onClick={toggle}
            onKeyDown={(e) => onActivateKey(e, toggle)}
         >
            <div className="event-log-cell severity"><SeverityBadge severity={failure.severity} /></div>
            <div className="event-log-cell time">{formatDate(failure.logDate)}</div>
            <div className="event-log-cell user" title={failure.userName}>{failure.userName || "-"}</div>
            <div className="event-log-cell screen" title={failure.procedureName}>{failure.procedureName}</div>
            <div className="event-log-cell saw" title={failure.message}>{failure.message}</div>
            <div className="event-log-cell item" title={failure.moreInfo}>{failure.moreInfo || "-"}</div>
            <div className="event-log-cell entries">
               {failure.entryCount > 1 ? <span className="event-log-status">{failure.entryCount} entries</span> : null}
            </div>
         </div>
         {open ? <FailureDetail id={detailId} rows={failure.rows} requestId={failure.requestId} /> : null}
      </div>
   );
}


// The expanded layers of one failure (or the single row of the every-row view).
function FailureDetail({ id, rows, requestId }) {
   const [showDetails, setShowDetails] = useState(false);
   const hasException = _.some(rows, (r) => r.exceptionString || r.stackTrace);
   // The row the failure line describes - never the impersonation attribution row unless it is all there is, as on the server.
   const named = _.filter(rows, (r) => r.procedureName !== IMPERSONATION_LOG_SOURCE);
   const candidates = named.length ? named : rows;
   const headline = _.find(candidates, (r) => r.layer === LAYER_CONTROLLER) || candidates[0];
   return (
      <div className="event-log-detail" id={id}>
         {_.map(rows, (row) => (
            <LayerBlock key={row.logId} row={row} showDetails={showDetails} />
         ))}
         <div className="event-log-detail-foot">
            {hasException ? (
               <Button color="link" size="sm" className="event-log-link" aria-expanded={showDetails}
                  onClick={() => setShowDetails((prior) => !prior)}>
                  Details
               </Button>
            ) : <span className="event-log-secondary">No exception recorded</span>}
            {headline ? (
               <Button color="link" size="sm" className="event-log-link" onClick={() => copyToClipboard(String(headline.logId))}>
                  Copy Log # {headline.logId}
               </Button>
            ) : null}
            {requestId ? (
               <>
                  <Button color="link" size="sm" className="event-log-link" onClick={() => copyToClipboard(requestId)}>
                     Copy request id
                  </Button>
                  <span className="event-log-secondary">{requestId}</span>
               </>
            ) : (
               <span className="event-log-secondary">
                  No request id (logged before request ids, or outside a request) - this entry stands alone
               </span>
            )}
         </div>
      </div>
   );
}


function LayerBlock({ row, showDetails }) {
   return (
      <div className="event-log-layer">
         <div className="event-log-layer-head">
            <span className="event-log-tag">{row.layer}</span>
            <span className="event-log-proc">{row.procedureName}</span>
            <span className="event-log-secondary">{formatDate(row.logDate)} - Log # {row.logId}</span>
         </div>
         <div className="event-log-layer-msg">{row.message}</div>
         {row.moreInfo ? <div className="event-log-secondary">More info: {row.moreInfo}</div> : null}
         {showDetails && row.exceptionString ? <pre className="event-log-trace">{row.exceptionString}</pre> : null}
         {showDetails && row.stackTrace ? <pre className="event-log-trace">{row.stackTrace}</pre> : null}
      </div>
   );
}


function RowList({ rows, loaded, emptyText }) {
   if (rows.length === 0) {
      return <EmptyNote loaded={loaded} text={emptyText} />;
   }
   return (
      <div>
         <div className="event-log-columns flat" aria-hidden="true">
            <div className="event-log-cell severity">Severity</div>
            <div className="event-log-cell time">Time</div>
            <div className="event-log-cell user">User</div>
            <div className="event-log-cell screen">Screen / action</div>
            <div className="event-log-cell saw">Message</div>
            <div className="event-log-cell logid">Log #</div>
         </div>
         {_.map(rows, (row) => (
            <RowItem key={row.logId} row={row} />
         ))}
      </div>
   );
}


function RowItem({ row }) {
   const [open, setOpen] = useState(false);
   const detailId = `event-log-row-${row.logId}`;
   const toggle = () => setOpen((prior) => !prior);
   return (
      <div className="event-log-failure">
         <div
            className="event-log-row flat"
            role="button"
            tabIndex={0}
            aria-expanded={open}
            aria-controls={detailId}
            onClick={toggle}
            onKeyDown={(e) => onActivateKey(e, toggle)}
         >
            <div className="event-log-cell severity"><SeverityBadge severity={severityOfType(row.typeId)} /></div>
            <div className="event-log-cell time">{formatDate(row.logDate)}</div>
            <div className="event-log-cell user" title={row.userName}>{row.userName || "-"}</div>
            <div className="event-log-cell screen" title={row.procedureName}>{row.procedureName}</div>
            <div className="event-log-cell saw" title={row.message}>{row.message}</div>
            <div className="event-log-cell logid">{row.logId}</div>
         </div>
         {open ? <FailureDetail id={detailId} rows={[row]} requestId={row.requestId} /> : null}
      </div>
   );
}


// A typed username, with a preferred line break before an email's "@" so a wrapped name reads "first.last" /
// "@servicethread.com" instead of breaking at the column edge (the cell still breaks anywhere for a longer part).
function UsernameText({ username }) {
   const at = username.indexOf("@");
   if (at <= 0) return username;
   return <>{username.slice(0, at)}<wbr />{username.slice(at)}</>;
}


function SignInList({ signIns, loaded }) {
   if (signIns.length === 0) {
      return <EmptyNote loaded={loaded} text="No failed sign-ins match these filters." />;
   }
   return (
      <div>
         <div className="event-log-columns" aria-hidden="true">
            <div className="event-log-cell time">Time</div>
            <div className="event-log-cell user signin">Username</div>
            <div className="event-log-cell ip">IP</div>
            <div className="event-log-cell saw">Reason</div>
         </div>
         {_.map(signIns, (attempt, index) => (
            <div className="event-log-row static" key={`${attempt.occurredAt}-${index}`}>
               <div className="event-log-cell time">{formatDate(attempt.occurredAt)}</div>
               <div className="event-log-cell user signin wrap" title={attempt.username}>{attempt.username ? <UsernameText username={attempt.username} /> : "-"}</div>
               <div className="event-log-cell ip">{attempt.ip || "-"}</div>
               <div className="event-log-cell saw wrap">{attempt.reason || "-"}</div>
            </div>
         ))}
      </div>
   );
}
